import { useState } from "react"
import { router } from "@inertiajs/react"
import MediaGallery from "@/components/storefront/MediaGallery"
import CollapsibleSection from "@/components/storefront/CollapsibleSection"
import Breadcrumb from "@/components/storefront/Breadcrumb"
import type { BreadcrumbCrumb } from "@/components/storefront/Breadcrumb"
import type {
  ItemDetailData,
  MediaItem,
  Variant,
} from "@/components/storefront/types"
import type { TttItemAuthor, TttItemLabels } from "@/components/ttt/types"

interface TttItemDetailProps {
  item: ItemDetailData
  medias: MediaItem[]
  variants: Variant[]
  virtualStock: number
  store: { holiday: boolean; display_stock: boolean }
  addToCartUrl: string
  back: { url: string; label: string }
  breadcrumb: BreadcrumbCrumb[]
  labels: TttItemLabels
  authors: TttItemAuthor[]
  authorsActive: boolean
}

// Faithful React port of app/views/items/_show.html.slim (TTT), keeping the
// added back button + breadcrumb: description visible under the price, an
// "Informations" (specifications) disclosure and an "Auteur·ices" disclosure.
export default function ItemDetail({
  item,
  medias,
  variants,
  virtualStock,
  store,
  addToCartUrl,
  back,
  breadcrumb,
  labels,
  authors,
  authorsActive,
}: TttItemDetailProps) {
  const isPreSale = item.pre_sale || item.internal_pre_sale
  const defaultVariant = variants[0]

  const addDefault = (e: React.FormEvent) => {
    e.preventDefault()
    if (!defaultVariant) return
    router.post(
      addToCartUrl,
      { variant_id: defaultVariant.id },
      { preserveScroll: true, preserveState: true }
    )
  }

  return (
    <div className="bg-white min-h-screen pt-36">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:max-w-7xl lg:px-8 pt-8">
        <a
          href={back.url}
          className="inline-flex items-center border border-contrast rounded-md px-4 py-2 text-sm font-medium text-contrast hover:bg-contrast hover:text-light transition-colors"
        >
          <i className="fa-solid fa-arrow-left mr-2" />
          {back.label}
        </a>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24 lg:max-w-7xl lg:px-8">
        <Breadcrumb items={breadcrumb} className="mb-6" />
        <div className="lg:flex lg:gap-x-8 lg:items-start">
          <div className="lg:w-[58.333%]">
            <MediaGallery medias={medias} zoomScale={2.5} />
          </div>

          <div className="mt-10 px-4 sm:mt-16 sm:px-0 lg:mt-0 lg:w-[41.667%] lg:pl-8 lg:self-stretch">
            <div className="lg:sticky lg:top-36">
              <h1 className="text-3xl font-bold tracking-tight text-contrast">
                {item.name}
              </h1>

              {isPreSale && (
                <div className="mt-3 cursor-help">
                  <span
                    title={labels.pre_sale_tooltip ?? undefined}
                    className="inline-block bg-gray-200 text-content text-sm font-medium px-3 py-1 rounded whitespace-nowrap"
                  >
                    {labels.pre_sale}
                  </span>
                </div>
              )}

              <div className="mt-3">
                <h2 className="sr-only">{labels.product_information}</h2>
                <p className="text-3xl tracking-tight text-contrast">
                  {item.price}
                </p>
              </div>

              {item.description && (
                <div className="mt-6">
                  <div className="space-y-6 text-base text-contrast">
                    <p>{item.description}</p>
                  </div>
                </div>
              )}

              {variants.length > 1 ? (
                <TttVariantSelector
                  variants={variants}
                  addToCartUrl={addToCartUrl}
                  labels={labels}
                />
              ) : (
                <TttSinglePurchase
                  item={item}
                  virtualStock={virtualStock}
                  holiday={store.holiday}
                  displayStock={store.display_stock}
                  labels={labels}
                  onSubmit={addDefault}
                />
              )}

              <section className="mt-12" aria-labelledby="details-heading">
                <h2 id="details-heading" className="sr-only">
                  {labels.product_information}
                </h2>
                <div className="divide-y divide-content border-t">
                  <CollapsibleSection title={labels.information} defaultOpen={false}>
                    <ul className="list-disc leading-8 pl-3 text-midgray">
                      {item.specifications.map((spec) => (
                        <li key={spec}>{spec}</li>
                      ))}
                    </ul>
                  </CollapsibleSection>

                  {authorsActive && authors.length > 0 && (
                    <CollapsibleSection title={labels.authors} defaultOpen={false}>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {authors.map((author) => (
                          <a
                            key={author.url}
                            href={author.url}
                            className="group flex flex-col items-center text-center transition-all duration-300 hover:scale-110 cursor-pointer"
                          >
                            <div className="mb-2 relative transition-all duration-300 group-hover:rotate-6">
                              {author.avatar_url ? (
                                <img
                                  src={author.avatar_url}
                                  alt={author.nickname}
                                  className="w-20 h-20 rounded-full object-cover border-2 border-content transition-all duration-300 group-hover:border-primary group-hover:shadow-lg"
                                />
                              ) : (
                                <div className="w-20 h-20 rounded-full bg-light border-2 border-content flex items-center justify-center transition-all duration-300 group-hover:border-primary group-hover:shadow-lg">
                                  <i className="fa-solid fa-user text-midgray transition-colors duration-300 group-hover:text-primary" />
                                </div>
                              )}
                            </div>
                            <div className="text-xs font-medium text-contrast transition-all duration-300 group-hover:text-primary group-hover:font-bold">
                              {author.nickname}
                            </div>
                          </a>
                        ))}
                      </div>
                    </CollapsibleSection>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function TttVariantSelector({
  variants,
  addToCartUrl,
  labels,
}: {
  variants: Variant[]
  addToCartUrl: string
  labels: TttItemLabels
}) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const selected = variants.find((v) => v.id === selectedId) ?? null

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selected || selected.available_stock <= 0) return
    router.post(
      addToCartUrl,
      { variant_id: selected.id },
      { preserveScroll: true, preserveState: true }
    )
  }

  const stockInfo = (() => {
    if (!selected) return ""
    if (selected.available_stock <= 0) return labels.out_of_stock
    if (selected.available_stock === 1) return labels.last_one
    return labels.in_stock_template.replace("{count}", String(selected.available_stock))
  })()

  return (
    <div className="mt-6">
      <h3 className="text-sm font-medium text-contrast mb-4">
        {labels.select_variant}
      </h3>
      <div className="flex flex-wrap gap-3">
        {variants.map((variant) => {
          const available = variant.available_stock > 0
          const isSelected = selectedId === variant.id
          return (
            <label
              key={variant.id}
              className={`relative ${
                available ? "cursor-pointer" : "cursor-not-allowed opacity-50"
              }`}
            >
              <input
                type="radio"
                name="variant_id"
                value={variant.id}
                disabled={!available}
                checked={isSelected}
                onChange={() => setSelectedId(variant.id)}
                className="peer sr-only"
              />
              <span
                className={`flex items-center justify-center px-6 py-3 text-sm font-medium text-contrast bg-light rounded-md border-2 border-transparent peer-checked:border-contrast hover:bg-contour transition-colors duration-200 ${
                  available ? "" : "line-through"
                }`}
              >
                {variant.label}
              </span>
              {!available && (
                <span className="absolute top-0 right-0 transform translate-x-1 -translate-y-1 bg-red-500 text-white text-xs px-1 rounded">
                  {labels.soldout_short}
                </span>
              )}
            </label>
          )
        })}
      </div>

      <form onSubmit={submit} className="mt-4">
        <button
          type="submit"
          disabled={!selected || selected.available_stock <= 0}
          className="btn btn-primary mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {labels.add_to_cart}
        </button>
        {stockInfo && <p className="text-sm text-midgray">{stockInfo}</p>}
      </form>
    </div>
  )
}

function TttSinglePurchase({
  item,
  virtualStock,
  holiday,
  displayStock,
  labels,
  onSubmit,
}: {
  item: ItemDetailData
  virtualStock: number
  holiday: boolean
  displayStock: boolean
  labels: TttItemLabels
  onSubmit: (e: React.FormEvent) => void
}) {
  if (item.pre_sale && item.pre_sale_url) {
    return (
      <div className="mt-6">
        <a
          href={item.pre_sale_url}
          target="_blank"
          rel="noreferrer"
          className="btn btn-primary mb-4"
        >
          {labels.participate_crowdfunding}
        </a>
      </div>
    )
  }

  if (holiday) return <div className="mt-6" />

  const addButton = (
    <form onSubmit={onSubmit}>
      <button type="submit" className="btn btn-primary mb-4">
        {labels.add_to_cart}
      </button>
    </form>
  )

  if (displayStock) {
    if (virtualStock > 0) {
      return (
        <div className="mt-6">
          {addButton}
          <p className="text-sm text-midgray">{labels.in_stock}</p>
        </div>
      )
    }
    return <p className="mt-6 font-bold text-red-500">{labels.out_of_stock}</p>
  }

  if (virtualStock > 1) return <div className="mt-6">{addButton}</div>
  if (virtualStock === 1) {
    return (
      <div className="mt-6">
        {addButton}
        <p className="text-sm text-primary">{labels.last_one}</p>
      </div>
    )
  }
  return <p className="mt-6 font-bold text-red-500">{labels.out_of_stock}</p>
}
